import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

/**
 * Egységes megerősítő ablak a natív `window.confirm()` helyett.
 *
 * MIÉRT (webes UX-elemzés 6.4): 11 helyen a böngésző saját ablaka kérdezett rá veszélyes
 * műveletekre (esemény/üzenet/kifizetés/megjegyzés/profilkép/elérhetőség törlése, résztvevő
 * eltávolítása, helyettesítés kérése). A natív ablak nem témázható (sötét módban rendszer-stílusú
 * marad), a webcímet írja a kérdés fölé, csak igen/nem lehet benne, a veszélyes gombja nem lehet
 * vörös, megállítja a JS-szálat, és a felhasználó a böngészőben ki tudja kapcsolni az ilyen
 * ablakokat — onnantól a törlés kérdés NÉLKÜL fut le.
 *
 * Használat:
 *   const confirm = useConfirm();
 *   if (!(await confirm({ message: '…', confirmLabel: 'Törlés', destructive: true }))) return;
 */

export interface ConfirmOptions {
  /** Fejléc; ha nincs, a „Megerősítés" szerepel. */
  title?: string;
  /** A kérdés szövege. */
  message: React.ReactNode;
  /** A megerősítő gomb felirata (alapban „Rendben"). */
  confirmLabel?: string;
  /** A kilépő gomb felirata (alapban „Mégse"). */
  cancelLabel?: string;
  /** Veszélyes (törlő) művelet: a megerősítő gomb vörös. */
  destructive?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/**
 * [7. javító kör — validátor F1, 2026-10-04] A függőben lévő kérdés kívülről lezárása „Mégse"-ként.
 *
 * MI VOLT A BAJ: a provider EGYETLEN Dialogot tart. Ha a lejárat pillanatában már nyitva állt egy
 * kérdés (pl. „Mentetlen változás — elvetés?"), az újra-belépő ablak FÖLÉ nyílt; az ablak
 * „Kijelentkezés" gombja a kérdés TARTALMÁT cserélte, de az a takarásban maradt (a gomb halottnak
 * tűnt — mérve valódi Chrome-ban, `elementFromPoint`, validátor `vsc2/q3.spec.ts`). Újra-belépés
 * után az elavult „Kijelentkezés" kérdés előjött, és igen-re a FRISS munkamenetet is kidobta.
 *
 * A „Mégse" a biztonságos irány: minden hívó `false`-ra a műveletet NEM végzi el, a beírt adat
 * MEGMARAD (a „Mentetlen változás — elvetés?" kérdés „Mégse"-je épp a megtartás).
 */
export interface ConfirmControl {
  /** A függőben lévő kérdés (ha van) lezárása `false`-szal; utána a dialógus bezárul. */
  cancelPending: () => void;
}

const ConfirmControlContext = createContext<ConfirmControl | null>(null);

interface PendingConfirm extends ConfirmOptions {
  resolve: (confirmed: boolean) => void;
}

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // A tartalom a záró animáció VÉGÉIG megmarad (`open` külön a `pending`-től): különben a
  // halványodás közben a fejléc visszaugrik „Megerősítés"-re és a vörös „Törlés" gomb kék
  // „Rendben"-re vált — a felhasználó egy pillanatra más kérdést lát, mint amit megválaszolt.
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const [open, setOpen] = useState(false);
  const pendingRef = useRef<PendingConfirm | null>(null);
  const openRef = useRef(false);
  /**
   * [7. javító kör — validátor F1, 2026-10-04] A Dialog PÉLDÁNY-kulcsa. Egy ZÁRT (vagy épp
   * halványodó) ablakba érkező új kérdés FRISS példányt kap. MÉRVE valódi Chrome-ban: ha a kérdés a
   * még csatolt, halványodó példányban nyílt újra (pl. az újra-belépő ablak nyitásakor lezárt
   * kérdés után azonnal a „Kijelentkezés"), a MUI modal-kezelője NEM vette újra a legfelső ablaknak:
   * a kérdés `aria-hidden` maradt (képernyőolvasó és `getByRole` nem látta), és a fókusz-csapda az
   * újra-belépő ablaké volt — billentyűzettel elérhetetlen. A friss példány a DOM végére kerül, és a
   * modal-kezelő a legfelsőként veszi fel. Nyitott ablakban a tartalom-csere (régi viselkedés) marad.
   */
  const [instance, setInstance] = useState(0);

  /**
   * [8. javító kör — 6. validátor M-a, 2026-10-04] KEZDŐ FÓKUSZ, kézzel.
   *
   * MI VOLT A BAJ: a gombok `autoFocus`-a csak jsdom-ban hatott (a Jest-teszt zöld volt), valódi
   * Chrome-ban a fókusz a `DIV.MuiDialog-container`-en állt — önálló kérdésnél ÉS nyitott dialógus
   * fölötti kérdésnél is, 0/50/500 ms-mal mérve (scratchpad `v6/p3.spec.ts`, `r8/fokusz.spec.ts`).
   * A MUI fókusz-csapdája a megnyíláskor a tárolót fókuszálja. Az Enter így semmit sem tett (nem
   * is törölt), de a komment szerinti „Mégse kapja a fókuszt" nem volt igaz.
   * MOST: a nyitó animáció elején (`onEntering`) és végén (`onEntered`, tartalék) a kívánt gombra
   * állítjuk a fókuszt a „Mégse" gombra — CSAK veszélyes (`destructive: true`) kérdésnél. A tartalék
   * csak akkor lép, ha a fókusz még NEM egy gombon áll a kérdésben (a felhasználó közben arrébb
   * léphetett).
   *
   * [Web UX 2. csomag, 2026-10-04 — koordinátori döntés] NEM-ROMBOLÓ kérdésnél SZÁNDÉKOSAN nem
   * fókuszálunk gombot: a fókusz marad a MUI tárolón (ez az éles viselkedés), így az Enter NEM
   * erősít meg semmit. OKA: a megerősítő gomb kezdő fókusza valódi böngészőben ÚJ viselkedést hozna
   * élesbe — pl. a „Kifizetve" státusznál vagy a „Helyettesítést kérek"-nél egy véletlen Enter
   * pénzügyi / szervezési műveletet indítana megerősítés nélkül. Őr: `ConfirmContext.test.tsx`
   * („nem-romboló kérdésnél … az Enter NEM erősít meg") és `flow-confirm-dialog.spec.ts`.
   */
  const cancelButtonRef = useRef<HTMLButtonElement | null>(null);
  const focusInitialButton = useCallback((onlyIfUnfocused: boolean) => {
    if (!pendingRef.current?.destructive) return;
    const target = cancelButtonRef.current;
    if (!target) return;
    if (onlyIfUnfocused) {
      const active = document.activeElement;
      const dialogRoot = target.closest('[role="dialog"]');
      if (active && active.tagName === 'BUTTON' && dialogRoot?.contains(active)) return;
    }
    target.focus();
  }, []);

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      // Ha valamiért már áll egy megválaszolatlan kérdés, azt elutasítottnak vesszük — így a
      // hívója nem marad örökre az `await`-en (a veszélyes művelet pedig NEM fut le).
      pendingRef.current?.resolve(false);
      const next = { ...options, resolve };
      pendingRef.current = next;
      if (!openRef.current) setInstance((n) => n + 1);
      openRef.current = true;
      setPending(next);
      setOpen(true);
    });
  }, []);

  const settle = useCallback((confirmed: boolean) => {
    const current = pendingRef.current;
    pendingRef.current = null;
    openRef.current = false;
    setOpen(false);
    current?.resolve(confirmed);
  }, []);

  const control = useMemo<ConfirmControl>(
    () => ({
      cancelPending: () => {
        if (pendingRef.current) settle(false);
      },
    }),
    [settle]
  );

  return (
    <ConfirmControlContext.Provider value={control}>
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog
        key={instance}
        open={open}
        onClose={() => settle(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="confirm-dialog-title"
        // [7. javító kör — validátor F1, 2026-10-04] A NYITOTT kérdés a többi ablak fölött: a MUI azonos
        // `zIndex`-ű ablakai közül a DOM-ban később csatolt van felül. Ha a kérdés-ablak még a záró
        // animációban volt (csatolva maradt), amikor egy MÁSIK ablak (pl. az újra-belépő) nyílt, egy
        // újabb kérdés az alatt jelent meg. Egy kérdés mindig a felhasználó azonnali válaszára vár,
        // tehát nem lehet takarásban. CSAK nyitott állapotban: a lezárt (halványodó) kérdés ne takarja
        // a helyette nyíló ablakot (mérve: a „Mentetlen változás" záró animációja ~200 ms-ig az
        // újra-belépő ablak FÖLÖTT volt, és elnyelte a kattintást).
        sx={{ zIndex: (theme) => (open ? theme.zIndex.modal + 1 : theme.zIndex.modal) }}
        data-testid="confirm-dialog"
        TransitionProps={{
          // [8. javító kör, M-a] kezdő fókusz valódi böngészőben (lásd `focusInitialButton`)
          onEntering: () => focusInitialButton(false),
          onEntered: () => focusInitialButton(true),
          onExited: () => {
            // Közben nyílhatott egy újabb kérdés — azt nem szabad kiürítenünk.
            if (!openRef.current) setPending(null);
          },
        }}
      >
        <DialogTitle id="confirm-dialog-title">{pending?.title ?? 'Megerősítés'}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div" sx={{ whiteSpace: 'pre-line' }}>
            {pending?.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {/* Veszélyes műveletnél a „Mégse" kapja a kezdő fókuszt: az Enter-lenyomás így nem
              töröl azonnal. Nem-romboló kérdésnél NINCS kezdő gomb-fókusz (a tároló a fókuszált,
              az Enter nem erősít meg) — [Web UX 2. csomag, 2026-10-04] döntés, lásd fent.
              [8. javító kör, M-a] Ezt a `focusInitialButton` (TransitionProps) végzi. A korábbi
              `autoFocus` valódi Chrome-ban NEM hatott, csak jsdom-ban — a Jest-őrt hamis zöldre
              állította, ezért kikerült: így a Jest-őr is a valódi mechanizmust méri. */}
          <Button
            ref={cancelButtonRef}
            onClick={() => settle(false)}
          >
            {pending?.cancelLabel ?? 'Mégse'}
          </Button>
          <Button
            onClick={() => settle(true)}
            variant="contained"
            color={pending?.destructive ? 'error' : 'primary'}
          >
            {pending?.confirmLabel ?? 'Rendben'}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
    </ConfirmControlContext.Provider>
  );
};

/**
 * A függőben lévő kérdés lezárásának módja (lásd `ConfirmControl`). Provider nélkül üres művelet:
 * a natív `window.confirm` tartalék szinkron, ott nincs mit lezárni.
 */
export function useConfirmControl(): ConfirmControl {
  return useContext(ConfirmControlContext) ?? NOOP_CONTROL;
}

const NOOP_CONTROL: ConfirmControl = { cancelPending: () => {} };

/**
 * Megerősítés kérése. Provider nélkül (pl. egy régi, providert nem körbetevő teszt) a KORÁBBI
 * viselkedésre, a natív `window.confirm`-ra esik vissza — így soha nem fordulhat elő, hogy egy
 * hiányzó provider miatt egy törlés kérdés nélkül lefut.
 */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (ctx) return ctx;
  return fallbackConfirm;
}

const fallbackConfirm: ConfirmFn = ({ title, message }) => {
  const text = [title, typeof message === 'string' ? message : ''].filter(Boolean).join('\n\n');
  // eslint-disable-next-line no-restricted-globals
  return Promise.resolve(window.confirm(text || 'Megerősíted?'));
};

export default ConfirmProvider;
